import { hooksCustomizatioKeyPrefix } from '../services/hooks'
import { Customizations, FontFamilyStyle, WidgetCustomizations } from './types'

export const getWidgetCustomizations = (customizations: Customizations): WidgetCustomizations => {
  return {
    isReadOnly: getBoolean(customizations, 'mode-read-only'),
    isPreview: getBoolean(customizations, 'mode-preview'),
    isLaunched: getBooleanWithDefault(customizations, 'is-launched', false),
    isDresser: getBooleanWithDefault(customizations, 'is-dresser', false),
    livePreview: {
      show: getBooleanWithDefault(customizations, 'show-live-preview', false),
      widgetMarkingStrategy: customizations['live-preview-widget-marking-strategy'] || 'blinking_border',
      topBar: {
        headerText: customizations['top-bar-header-text'] || 'How does it look?',
        helpText: customizations['top-bar-help-text'] || 'This is still a preview. It won’t go live until you launch your subscription.',
        backgroundColor: customizations['top-bar-background-color'] || '#0042E4',
        backgroundOpacity: customizations['top-bar-background-opacity'] || 0.95,
        fontColor: customizations['top-bar-font-color'] || '#ffffff',
        ctaLinkPath: customizations['top-bar-cta-link-path'] || '',
        ctaText: customizations['top-bar-cta-text'] || 'Get live support',
        closeButtonTextColor: customizations['top-bar-cta-text-color'] || '#0042E4',
        closeButtonBackgroundColor: customizations['top-bar-cta-background-color'] || '#ffffff',
        closeButtonText: customizations['top-bar-close-button-text'] || 'Looks good, back to setup'
      }
    },
    isDummy: getBoolean(customizations, 'mode-dummy'),
    autoPickOptionIndex: getNumber(customizations, 'auto-pick-option-index', 0),
    productHandle: customizations['product-handle'],
    storefrontTheme: customizations['storefront-theme'],
    buyOnceText: customizations['buy-once-text'] || 'Buy once',
    oneTimeAtLast: getBooleanWithDefault(customizations, 'one-time-at-last', false),
    showGetItText: getBoolean(customizations, 'show-get-it-text'),
    getItText: customizations['get-it-text'] || 'Get it',
    sellingPlans: {
      showDiscount: getBooleanWithDefault(customizations, 'selling-plans-show-discount', true),
      sort: {
        strategy: customizations['selling-plans-sort-strategy'] || 'frequency'
      }
    },
    tiles: {
      borderStyle: customizations['styles-tile-border-style'] || 'solid',
      borderColor: customizations['styles-tile-border-color'] || '#2CCCD3',
      borderSize: getPixel(customizations, 'styles-tile-border-size', '2px'),
      backgroundColor: customizations['styles-tile-background-color'] || '#2CCCD3',
      fontColor: customizations['styles-tile-font-color'] || '#0042E4',
      fullPriceText: customizations['tiles-label-text'] || 'full price',
      suffixText: customizations['tiles-label-suffix-text'] || ' off',
      tileHeight: getPixel(customizations, 'tiles-tile-height', '70px'),
      oddSpread: getBooleanWithDefault(customizations, 'tiles-odd-spread', false)
    },
    selectionType: customizations['plan-selection-type'] || 'tiles-only',
    autoInject: {
      enabled: getBooleanWithDefault(customizations, 'should-auto-inject', false),
      targetSelector: customizations['auto-inject-target-selector'] || '',
      adjacentStrategy: customizations['auto-inject-adjacent-strategy'] || ''
    },
    fullFormatProductPrice: sanitizeHtml(customizations, 'full-format-product-price', '$16.99'),
    priceLabel: {
      prefix: customizations['price-label-prefix'] || 'From',
      strategy: customizations['price-label-strategy'] || 'starting_and_actual',
      percentSymbolLocation: customizations['price-label-percent-symbol-location'] || 'right'
    },
    subscriptionPolicy: {
      shouldShow: getBooleanWithDefault(customizations, 'subscription-policy-should-show', false),
      description: customizations['subscription-policy-description'] || 'You\'re subscribing to receive this item multiple times, on a recurring basis (according to the frequency you select) with a discount on every recurring order. You may cancel or change your subscription at any time.',
      footer: customizations['subscription-policy-footer'] || 'View subscription policy',
      informationLink: customizations['subscription-policy-information-link']
    },
    styles: {
      notSelectedRadio: {
        backgroundColor: customizations['styles-not-selected-radio-background-color'] || '#F1F1F1'
      },
      selectedRadio: {
        borderStyle: customizations['styles-selected-radio-border-style'] || 'solid',
        borderColor: customizations['styles-selected-radio-border-color'] || '#dbd9d4',
        borderSize: getPixel(customizations, 'styles-selected-radio-border-size', '1px'),
        backgroundColor: customizations['styles-selected-radio-background-color'] || 'rgba(0, 66, 228, 10)',
        backgroundImageUrl: customizations['styles-selected-radio-background-image-url'] || 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAAAdgAAAHYBTnsmCAAAABl0RVh0U29mdHdhcmUAd3d3Lmlua3NjYXBlLm9yZ5vuPBoAAALdSURBVDiNfZNNSNNxGMe//9/UOefe/G+Z868z30gTnK+JaVqEqeQoO3WIooxOehBEAukUBR0U6RDYpUt0KMt3i0QJwsq5aepEndp06Zz/uc2XOTd1/w7iNLO+p+fwfL7P88DzpXBEqan52ZERqkr6hCoxMChESgiP87hdTpt9bsI6b36mH+3THe6n9guGYQTJSXlN2TnXi8VSiXzdMQZRKAEAbLopiOk02Fmr7fu35u7FxdEKg8Hg9RswDCNITbnQVaSpKlie76DKryQhPS3+j810eiNaPxgRHqXhulvre03zulKDweDlAUBOdvHLkqvVZW5HF1VXUwqlkj56GZQRNM7nxqG3p406k3n3lMVsjJ6ZHWvhqdUFWQWXbj/ctPcL62pKQQj5C94XIRRyzyags6OLUqryo7Y3lnpIpFJVJZVIFeVlSf+FnzzvwZ0HzfBseaEpiQNNh9N0eGwlCaOZRNfqONLVcQCAwRETuGNgLXcZPsIHRwFZGYlw2vSQyZnTJIgfKhUK9hAr68DjVz9R/ykQK461Q3ARxAutaKwthDAkGAAgFHAQBIukAQDnnxiukCEvgUP/pBfDfV+QoPDhB18D8UIbGmsLIRGHHnoACts7HkK8Hpdzw3WwdHXFReTy++BWlmBIdBOi42AALheHHa/bQVjrvFEkS4FObzwwubdnIpp6cSw8MDgJmSITy8tz0wFLy+ZG56qz6F3HhEKdGgcej/hN/qWV9WhYl8yszbLUQIaHP2u1X99+jFBpuKcNnfD5fP8EAeB18wgs9khOr23p1o/26XgAQIiny2FbPJecdiumvb2TEgs5RCrlfojjOIxP/ILFHosZ80nu/ZtHvTOzAzfsdvuuP0zx8fH82JiMpvSca6VhUrl8zTEMoYDCrm8XHm8AJGFqsKyFHdK2dk9ND9w3mUxbwKE07istpTAzgomqClMwiUGBwTJQFLyeTQfLmmbYRWv90Tj/BluALmoQILjqAAAAAElFTkSuQmCC',
        dropdownBorderColor: customizations['styles-selected-radio-dropdown-border-color'] || '#676A6C',
        dropdownBackgroundColor: customizations['styles-radio-button-dropdown-background-color'] || '#FCFCFC',
        dropdownHoverColor: customizations['styles-radio-button-dropdown-hover-color'] || 'whitesmoke',
        openByDefault: getBooleanWithDefault(customizations, 'styles-delected-radio-open-by-default', true)
      },
      radioButton: {
        innerColor: customizations['styles-radio-button-inner-color'] || '#0042E4',
        outerColor: customizations['styles-radio-button-outer-color'] || '#474747'
      },
      fontFamily: getFontFamily(customizations, 'styles-font-family', 'Nunito Sans@400|https://fonts.googleapis.com/css?family=Nunito+Sans:400&display=swap'),
      fontSize: {
        primary: getPixel(customizations, 'style-primary-font-size', '13px'),
        sellingPlans: {
          name: getPixel(customizations, 'style-selling-plan-name-font-size', '13px'),
          options: getPixel(customizations, 'style-selling-plan-options-font-size', '13px')
        }
      },
      colors: {
        text: {
          primary: customizations['styles-colors-text-primary'] || '#474747'
        }
      }
    },
    hooks: {
      widgetReady: getHook(customizations, 'hooks-widget-ready'),
      sellingPlanChanged: getHook(customizations, 'hooks-selling-plan-changed')
    }
  }
}

const getBoolean = (customizations: Customizations, key: string): boolean => {
  const value = customizations[key] && customizations[key].toString() === 'true'
  return value === true
}

const getBooleanWithDefault = (customizations: Customizations, key: string, defaultValue: boolean): boolean => {
  if (typeof customizations[key] === 'undefined') {
    return defaultValue
  }
  return getBoolean(customizations, key)
}

const getNumber = (customizations: Customizations, key: string, defaultValue: number): number => {
  const value = customizations[key] || defaultValue
  return parseInt(value, 10)
}

const getFontFamily = (customizations: Customizations, key: string, defaultValue: string): FontFamilyStyle => {
  const value = customizations[key] || defaultValue
  const values = value.replace('|', '@').split('@')
  return {
    name: values[0],
    weight: values[1],
    url: values[2]
  }
}

const getPixel = (customizations: Customizations, key: string, defaultValue: string): string => {
  const value = customizations[key] || defaultValue
  if (value && value.toString() && value.toString().indexOf('px') < 0) {
    return `${value}px`
  }

  return value
}

const getHook = (customizations: Customizations, key: string): string => {
  if (key.indexOf(hooksCustomizatioKeyPrefix) !== 0) {
    console.warn('hooks key must start with correct prefix')
    return ''
  }
  return customizations[key]
}

const sanitizeHtml = (customizations: Customizations, key: string, defaultValue: string) => {
  const value = customizations[key] || defaultValue
  return value.replace(/<\/?[^>]+(>|$)/g, '')
}
